/**
 * Changing a saved loom entry (owner or manager): edit it, move it to another day or saree, remove
 * it, or bring a removed one back. Every change is previewed first and then confirmed with the
 * preview's versions; the server checks it all again. These helpers only build the requests and
 * shape the server's answers for the screen.
 */
import { fmtRange } from "../rates/rateModel";
import { cleanSareeNumber } from "./sareeEditModel";

export type EntryAction = "REMOVE" | "RESTORE" | "MOVE_DATE" | "MOVE_TO_SAREE" | "EDIT";

type Person = { id?: string; code?: string; name?: string } | null | undefined;

/** One entry of the saree history as the server sends it (only what the change screens use). */
export type HistoryEntry = {
  id: string;
  date: string;
  status?: string;
  workTypeId?: string;
  workTypeCode?: string;
  workTypeName?: string;
  inches?: number | string;
  ladkaSool?: boolean;
  weaver?: Person;
  helper?: Person;
  paid?: boolean;
  canChange?: boolean;
  canRestore?: boolean;
  removed?: { at?: string | number; byName?: string; reason?: string; kind?: string; paidAmount?: number; paidSheetLabel?: string } | null;
  /** A manager's move of this entry waits for the owner; it can't be changed meanwhile. */
  moveWaiting?: boolean;
  movedTo?: { date?: string; sareeNumber?: string; entryId?: string } | null;
  movedFrom?: { date?: string; sareeNumber?: string; entryId?: string } | null;
};

/** The edit form: what the entry is now, as typed. */
export type EntryForm = {
  date: string;
  sareeNumber: string;
  weaverId: string;
  helperId: string;
  workTypeId: string;
  inches: string;
  ladkaSool: boolean;
  reason: string;
};

export type EntryChange = {
  entryId: string;
  action: EntryAction;
  toDate?: string;
  toSareeNumber?: string;
  weaverId?: string;
  /** A new helper, or "" with helperMode ALONE to drop the helper; left out keeps the helper. */
  helperId?: string;
  helperMode?: "WITH_HELPER" | "ALONE";
  workTypeId?: string;
  inches?: number;
  ladkaSool?: boolean;
};

export type ChangeRequest = { changes: EntryChange[]; reason?: string; adjustNextPaySheet: boolean };

/** A removed or moved entry stays in the history, struck through. */
export const isRemoved = (e: HistoryEntry): boolean => e.status === "VOID" || !!e.removed;

export const formFromEntry = (e: HistoryEntry, sareeNumber: string): EntryForm => ({
  date: e.date,
  sareeNumber: sareeNumber || "",
  weaverId: e.weaver?.id || "",
  helperId: e.helper?.id || "",
  workTypeId: e.workTypeId || "",
  inches: String(e.inches ?? ""),
  ladkaSool: !!e.ladkaSool,
  reason: "",
});

/**
 * What the form asks for: another saree (MOVE_TO_SAREE, maybe with a day), another day (MOVE_DATE),
 * or a change on the same day (EDIT). Only changed fields are sent; null when nothing changed.
 */
export const changeFromForm = (entryId: string, was: EntryForm, now: EntryForm): EntryChange | null => {
  const fields: Partial<EntryChange> = {};
  if (now.weaverId !== was.weaverId) fields.weaverId = now.weaverId;
  if (now.helperId !== was.helperId) {
    fields.helperId = now.helperId;
    fields.helperMode = now.helperId ? "WITH_HELPER" : "ALONE";
  }
  if (now.workTypeId !== was.workTypeId) fields.workTypeId = now.workTypeId;
  if (Number(now.inches) !== Number(was.inches)) fields.inches = Number(now.inches);
  if (now.ladkaSool !== was.ladkaSool) fields.ladkaSool = now.ladkaSool;
  const toSaree = cleanSareeNumber(now.sareeNumber);
  const dateMoved = !!now.date && now.date !== was.date;
  if (toSaree && toSaree !== cleanSareeNumber(was.sareeNumber)) {
    return { entryId, action: "MOVE_TO_SAREE", toSareeNumber: toSaree, ...(dateMoved ? { toDate: now.date } : {}), ...fields };
  }
  if (dateMoved) return { entryId, action: "MOVE_DATE", toDate: now.date, ...fields };
  return Object.keys(fields).length ? { entryId, action: "EDIT", ...fields } : null;
};

/** A reason is kept with every change, except a same-day edit of an entry not yet paid. */
export const reasonNeeded = (action: EntryAction, paid: boolean): boolean => action !== "EDIT" || paid;

/** First thing to fix in the form before it can be checked, as English text for t(); null when ready. */
export const formProblem = (was: EntryForm, now: EntryForm, today: string, paid: boolean): string | null => {
  if (!now.weaverId) return "Pick the weaver";
  if (!(Number(now.inches) > 0)) return "Inches must be more than 0";
  if (!now.date) return "Pick a date";
  if (now.date > today) return "The date can't be in the future";
  const change = changeFromForm("x", was, now);
  if (!change) return "Nothing changed yet";
  if (reasonNeeded(change.action, paid) && !now.reason.trim()) return "Say why";
  return null;
};

export const changeRequest = (changes: EntryChange[], reason: string, adjustNextPaySheet = false): ChangeRequest => ({
  changes,
  ...(reason.trim() ? { reason: reason.trim() } : {}),
  adjustNextPaySheet,
});

/** One remove (or restore) request for several entries: one reason, one batch. */
export const batchRequest = (action: "REMOVE" | "RESTORE", ids: string[], reason: string): ChangeRequest =>
  changeRequest(
    ids.map((entryId) => ({ entryId, action })),
    reason,
  );

// ---- The server's preview ------------------------------------------------------------------

export type Snapshot = {
  date?: string;
  sareeNumber?: string;
  loomCode?: string;
  workTypeCode?: string;
  inches?: number | string;
  weaverCode?: string;
  helperCode?: string | null;
  status?: string;
};

export type PreviewRow = {
  entryId: string;
  action: EntryAction;
  status: "OK" | "FIX" | "CONFIRM";
  code?: string | null;
  problem?: string | null;
  before?: Snapshot | null;
  after?: Snapshot | null;
  payBefore?: number | null;
  payAfter?: number | null;
  paid?: { sheetId?: string; sheetLabel?: string; amount?: number } | null;
  goesToApprovals?: boolean;
  version?: string | null;
};

export type RowView = {
  entryId: string;
  action: EntryAction;
  status: PreviewRow["status"];
  problem: string | null;
  before: Snapshot | null;
  after: Snapshot | null;
  /** Only when the server sends pay (roles that see wages). */
  pay: { before: number; after: number; delta: number } | null;
  paid: { label: string; amount: number | null } | null;
  goesToApprovals: boolean;
};

export type PreviewView = {
  rows: RowView[];
  summary: string;
  /** A row the server refused: nothing can be confirmed. */
  blocked: boolean;
  /** A paid row needs the owner's "adjust on next pay sheet". */
  needsAdjust: boolean;
  /** The adjust choice is offered: the server allows it and this is the owner. */
  canAdjust: boolean;
  versions: Record<string, string>;
};

const num = (v: unknown): number | null => (v === null || v === undefined || v === "" || !isFinite(Number(v)) ? null : Number(v));

export const previewView = (data: any, isOwner: boolean): PreviewView => {
  const raw: PreviewRow[] = Array.isArray(data?.rows) ? data.rows : [];
  const rows: RowView[] = raw.map((r) => {
    const before = num(r.payBefore);
    const after = num(r.payAfter);
    return {
      entryId: r.entryId,
      action: r.action,
      status: r.status,
      problem: r.status === "FIX" ? r.problem || codeText(r.code) || "Can't be changed" : null,
      before: r.before || null,
      after: r.after || null,
      pay: before === null && after === null ? null : { before: before ?? 0, after: after ?? 0, delta: Math.round(((after ?? 0) - (before ?? 0)) * 100) / 100 },
      paid: r.paid ? { label: fmtRange(r.paid.sheetLabel), amount: num(r.paid.amount) } : null,
      goesToApprovals: !!r.goesToApprovals,
    };
  });
  const versions: Record<string, string> = {};
  raw.forEach((r) => {
    if (r.version) versions[r.entryId] = r.version;
  });
  return {
    rows,
    summary: data?.summary || "",
    blocked: !rows.length || rows.some((r) => r.status === "FIX"),
    needsAdjust: rows.some((r) => r.status === "CONFIRM"),
    canAdjust: isOwner && !!data?.canAdjustNextPaySheet,
    versions,
  };
};

/** Confirm is possible when nothing is refused and a paid row has the owner's adjust choice. */
export const canConfirm = (view: PreviewView | null, adjust: boolean): boolean => !!view && !view.blocked && (!view.needsAdjust || (view.canAdjust && adjust));

/** The confirm: the previewed request again, with the versions it was checked against. */
export const confirmRequest = (req: ChangeRequest, view: PreviewView, clientRequestId: string, adjust: boolean) => ({
  ...req,
  adjustNextPaySheet: view.needsAdjust && adjust,
  clientRequestId,
  versions: view.versions,
});

/** "19 Oct 2026 · saree 18350 · S_1 · 14 in · K_273 + J_249" */
export const snapshotLine = (s: Snapshot | null | undefined, fmtDay: (iso: string) => string, sareeWord = "saree"): string => {
  if (!s) return "";
  const who = s.weaverCode ? `${s.weaverCode}${s.helperCode ? ` + ${s.helperCode}` : ""}` : "";
  return [
    s.date ? fmtDay(s.date) : "",
    s.sareeNumber ? `${sareeWord} ${s.sareeNumber}` : "",
    s.workTypeCode || "",
    s.inches !== undefined && s.inches !== null && s.inches !== "" ? `${s.inches} in` : "",
    who,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** "19 Oct · S_1 · 14 in · K_273": one entry of a ticked day, as the remove list shows it. */
export const entryLine = (e: HistoryEntry, fmtDay: (iso: string) => string): string =>
  snapshotLine({ date: e.date, workTypeCode: e.workTypeCode, inches: e.inches, weaverCode: e.weaver?.code, helperCode: e.helper?.code }, fmtDay);

// ---- Refusals ------------------------------------------------------------------------------

/** English text for t() when the server sends a code without a sentence. */
const CODE_TEXT: Record<string, string> = {
  STALE: "This entry changed meanwhile. Check again.",
  ENTRY_PAID: "This entry is already paid. Only the owner can take it back on the next pay sheet.",
  ENTRY_DUPLICATE: "The loom already has this part for this weaver on that day.",
  NO_RATE: "No approved rate on that day, so it would be paid nothing. Approve a rate first.",
  LOOM_CLOSED: "The loom is closed on that day.",
  NOT_ON_LOOM: "That saree is not on a loom on that day.",
  OUT_OF_SAREE_DATES: "That day is outside the saree's start and finish.",
  NOT_FOUND: "This entry is no longer there. Reload the page.",
  ENTRY_NOT_FOUND: "This entry is no longer there. Reload the page.",
  SAREE_NOT_FOUND: "That saree was not found.",
  INVALID: "Something in this change is not right. Check it and try again.",
  BUSY: "A pay sheet or another change is saving for this person or loom right now. Try again in a moment.",
  CONFLICT: "This can't be saved as it is.",
  NOT_SAVED: "Nothing was saved. Check again and try once more.",
  NOT_ALLOWED: "Only the owner or a manager can change this entry.",
  PERMISSION_DENIED: "Only the owner or a manager can change this entry.",
};

export const codeText = (code?: string | null): string | null => (code && CODE_TEXT[code]) || null;

/** What to show for a refused save: the server's sentence, else one for its code. */
export const problemText = (res: any): string => res?.message || codeText(res?.code) || "Could not save";

/** The entry changed under the preview: check it again before anything can be confirmed. */
export const needsFreshPreview = (code?: string | null): boolean => code === "STALE" || code === "NOT_FOUND" || code === "ENTRY_NOT_FOUND";

/**
 * After a refused save, the request id can be dropped only when nothing could have been written.
 * BUSY can mean the first send of this same id is still running, so a retry must reuse it.
 */
export const keepRequestId = (code?: string | null): boolean => code === "BUSY";

/**
 * A refused save lists every row as the server sees it now (without pay): rebuild the preview from
 * those rows, keeping the pay already shown, so a row that turned FIX blocks Confirm until it is
 * checked again. Null when the refusal carries no rows.
 */
export const refusalView = (old: PreviewView, data: any, isOwner: boolean): PreviewView | null => {
  if (!Array.isArray(data?.rows) || !data.rows.length) return null;
  const fresh = previewView(data, isOwner);
  const pay = new Map(old.rows.map((r) => [r.entryId, r.pay] as const));
  return {
    ...fresh,
    summary: fresh.summary || old.summary,
    canAdjust: fresh.canAdjust || old.canAdjust,
    rows: fresh.rows.map((r) => ({ ...r, pay: r.pay ?? pay.get(r.entryId) ?? null })),
    versions: { ...old.versions, ...fresh.versions },
  };
};

/** Refused because the entry was paid after the preview: preview again, so the owner sees the adjust choice. */
export const needsPaidRecheck = (code?: string | null): boolean => code === "ENTRY_PAID";

// ---- Change log ----------------------------------------------------------------------------

export type EntryChangeLog = {
  at?: string | number;
  byName?: string;
  role?: string;
  source?: string;
  action?: EntryAction | string;
  from?: Snapshot | null;
  to?: Snapshot | null;
  reason?: string | null;
  amountDelta?: number | null;
  paidSheetId?: string | null;
};

export const ACTION_LABEL: Record<string, string> = {
  REMOVED: "Removed",
  RESTORED: "Restored",
  MOVED_DATE: "Moved to another day",
  MOVED_SAREE: "Moved to another saree",
  EDITED: "Corrected",
  MOVE_REQUESTED: "Move asked for",
  MOVE_APPROVED: "Move approved",
  MOVE_REJECTED: "Move rejected",
  REMOVE: "Removed",
  RESTORE: "Restored",
  MOVE_DATE: "Moved to another day",
  MOVE_TO_SAREE: "Moved to another saree",
  EDIT: "Corrected",
  CREATE: "Entered",
  APPROVE: "Approved",
  REJECT: "Rejected",
};
